/**
 * Customer Portal (UI) - email/password login screen.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { authClient } from '../features/auth/client';
import type { ViewerSession } from '../types/portal';

type LoginRouteProps = {
  session: ViewerSession | null;
  onSessionRefresh: () => Promise<void>;
};

export function LoginRoute({ session, onSessionRefresh }: LoginRouteProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await (authClient.signIn as any).email({
        email: email.trim(),
        password
      });
      if (result?.error) {
        throw new Error(result.error.message ?? 'Invalid email or password');
      }
      await onSessionRefresh();
      navigate('/', { replace: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page page-center">
      <div className="container container-tight py-4">
        <div className="card card-md">
          <div className="card-body">
            <h2 className="h2 text-center mb-4">Sign in to Customer Portal</h2>
            <div className="alert alert-info" role="alert">
              <div className="fw-semibold mb-1">Portal hostname update</div>
              <div>
                <code>portal.prontoconnects.com</code> is now the messaging customer portal.
                If you are looking for the PBX portal, use{' '}
                <a href="https://pbx.portal.prontoconnects.com">pbx.portal.prontoconnects.com</a>.
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              {error ? <div className="alert alert-danger">{error}</div> : null}
              <div className="form-footer">
                <button className="btn btn-primary w-100" type="submit" disabled={submitting}>
                  {submitting ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
